import React, { useState, useCallback, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { searchV2FilterOptions, FILTER_GROUPS } from "config/filters";
import SearchPageContext from "pages/SearchPageV1/SearchPageV1Context";
import FiltersHeader from "./FiltersHeader";
import FilterSection from "./FilterSection";
import "./FiltersCard.scss";

const buildItemsFromFilterGroup = (groupKey) => {
	const group = searchV2FilterOptions[groupKey];

	if (!group) return [];

	return Object.values(group.options).map((option, index) => ({
		id: `${groupKey}-${index + 1}`,
		label: option.label,
		name: option.name,
		value: option.value,
		checked: option.checked || false
	}));
};

const buildSectionFromFilterGroup = (groupKey, title) => {
	const group = searchV2FilterOptions[groupKey];

	return {
		id: groupKey,
		title,
		multiple: group ? group.multiple : true,
		items: buildItemsFromFilterGroup(groupKey)
	};
};

const INITIAL_SECTIONS = [
	buildSectionFromFilterGroup(FILTER_GROUPS.AMENITIES, "Amenities"),
	buildSectionFromFilterGroup(FILTER_GROUPS.UNIT_SIZE, "Size")
];

const buildSectionsFromActiveFilters = (filters) => {
	if (!filters?.length) return INITIAL_SECTIONS;

	return filters
		.map((group, groupIndex) => {
			if (!group) return null;

			let title = group.label || `Group ${groupIndex + 1}`;
			if (group.label?.includes("Amenities")) {
				title = "Amenities";
			} else if (group.label?.includes("Size")) {
				title = "Size";
			}

			return {
				id: `${title}-${groupIndex}`,
				title,
				multiple: group.multiple,
				items: Object.values(group.options || {}).map(
					(option, index) => ({
						id: `${title}-${index + 1}`,
						label: option.label,
						name: option.name,
						value: option.value,
						checked: !!option.checked
					})
				)
			};
		})
		.filter(Boolean);
};

const FiltersCard = ({
	onChange = undefined,
	onFilterChange = undefined,
	navigateOnChange = false,
	activeFilters = undefined,
	onClose = undefined
}) => {
	const [sections, setSections] = useState(
		buildSectionsFromActiveFilters(activeFilters)
	);
	const { setIsFiltering } = useContext(SearchPageContext) || {};

	const handleNavigateOnChangeEffects = (shouldNavigate) => {
		if (!shouldNavigate) return;
		if (setIsFiltering) {
			setIsFiltering(true);
		}
		if (onClose) {
			onClose();
		}
	};

	// Keep local checkbox state in sync with Redux / URL-driven
	// filters whenever activeFilters changes.
	useEffect(() => {
		const nextSections = buildSectionsFromActiveFilters(activeFilters);
		setSections(nextSections);
	}, [activeFilters]);

	const emitChange = useCallback(
		(updatedSections) => {
			if (onChange) {
				onChange(updatedSections);
			}
		},
		[onChange]
	);

	const handleClear = () => {
		// Determine which filters are currently active. Prefer the live
		// Redux-driven activeFilters data when available so we clear any
		// filters set via query params or other UIs.
		const previouslyCheckedItems = [];
		if (activeFilters?.length) {
			activeFilters.forEach((group) => {
				Object.values(group.options).forEach((option) => {
					if (option.checked) {
						previouslyCheckedItems.push({
							name: option.name,
							value: option.value
						});
					}
				});
			});
		} else {
			sections.forEach((section) => {
				section.items.forEach((item) => {
					if (item.checked) {
						previouslyCheckedItems.push({
							name: item.name,
							value: item.value
						});
					}
				});
			});
		}

		const cleared = sections.map((section) => ({
			...section,
			items: section.items.map((item) => ({
				...item,
				checked: false
			}))
		}));
		setSections(cleared);
		emitChange(cleared);

		if (onFilterChange && previouslyCheckedItems.length > 0) {
			handleNavigateOnChangeEffects(navigateOnChange);
			previouslyCheckedItems.forEach((item, index) => {
				const isLast = index === previouslyCheckedItems.length - 1;
				const syntheticEvent = {
					target: {
						name: item.name,
						value: item.value,
						checked: false
					}
				};
				onFilterChange(syntheticEvent, navigateOnChange && isLast);
			});
		}
	};

	const handleItemChange = (sectionId, itemId) => {
		const updated = sections.map((section) => {
			if (section.id !== sectionId) return section;
			const isSingleSelect = section.multiple === false;
			return {
				...section,
				items: section.items.map((item) => {
					if (item.id === itemId) {
						return { ...item, checked: !item.checked };
					}
					if (isSingleSelect) {
						return { ...item, checked: false };
					}
					return item;
				})
			};
		});
		setSections(updated);
		emitChange(updated);

		if (onFilterChange) {
			const section = updated.find((s) => s.id === sectionId);
			if (section) {
				const toggledItem = section.items.find(
					(item) => item.id === itemId
				);
				if (toggledItem) {
					const syntheticEvent = {
						target: {
							name: toggledItem.name,
							value: toggledItem.value,
							checked: toggledItem.checked
						}
					};
					handleNavigateOnChangeEffects(navigateOnChange);
					onFilterChange(syntheticEvent, navigateOnChange);
				}
			}
		}
	};

	return (
		<section
			className="filters"
			aria-label="Filters"
		>
			<FiltersHeader
				title="Filters"
				onClear={handleClear}
				onClose={onClose}
			/>
			<div className="filters__content">
				{sections.map((section, index) => (
					<React.Fragment key={section.id}>
						<FilterSection
							id={section.id}
							title={section.title}
							items={section.items}
							onItemChange={handleItemChange}
						/>
						{index === 0 && <div className="filters__separator" />}
					</React.Fragment>
				))}
			</div>
		</section>
	);
};

FiltersCard.propTypes = {
	onChange: PropTypes.func,
	onFilterChange: PropTypes.func,
	navigateOnChange: PropTypes.bool,
	activeFilters: PropTypes.arrayOf(PropTypes.object),
	onClose: PropTypes.func
};

export default FiltersCard;
